import {
  QueryObserverResult,
  UseMutationResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { APIClient } from 'api/APIClient';
import {
  AllTravelRequestsResults,
  StatusObject,
  TravelRequestResult,
} from 'api/types/types';
import { QueryKeys } from 'constants/queryKeys';

async function getTravelRequests(
  role?: string
): Promise<TravelRequestResult[]> {
  const url = role ? `/request?role=${role}` : '/request';
  const { data } = await APIClient().get(url);
  return data;
}

export function useTravelRequestsQuery(
  role?: string
): QueryObserverResult<TravelRequestResult[], unknown> {
  return useQuery({
    queryKey: [QueryKeys.TRAVEL_REQUESTS, role],
    queryFn: () => getTravelRequests(role),
    enabled: true,
    refetchOnMount: 'always',
    staleTime: Infinity,
    retry: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja ferðabeiðnir. Reyndu aftur síðar',
    },
  });
}

async function updateTravelRequestStatus(
  id: string | null,
  statusType: StatusObject
): Promise<unknown> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const { data } = await APIClient().put(`request/${id}/state`, statusType);
  return data;
}

export function useUpdateTravelRequestStatus(
  id: string | null
): UseMutationResult<unknown, unknown, StatusObject, unknown> {
  return useMutation<unknown, unknown, StatusObject, unknown>({
    mutationFn: (statusType: StatusObject) =>
      updateTravelRequestStatus(id, statusType),
    mutationKey: [QueryKeys.TRAVEL_FORM_QUERY],
    meta: {
      successMessage: `Ferðabeiðni (${id}) hefur verið uppfærð`,
      errorMessage: 'Ekki tókst að uppfæra ferðabeiðnina. Reyndu aftur síðar',
    },
  });
}

async function getAllTravelRequests(
  dateFrom: string,
  dateTo: string,
  status?: string,
  searchText?: string | undefined
): Promise<AllTravelRequestsResults> {
  let baseUrl = `/request/list?role=admin&dateFrom=${dateFrom}&dateTo=${dateTo}`;
  if (status) baseUrl += `&status=${status}`;
  if (searchText) baseUrl += `&searchText=${searchText}`;
  const { data } = await APIClient().get(baseUrl);
  return data;
}

export function useGetAllTravelRequestsQuery(
  dateFrom: string,
  dateTo: string,
  status?: string,
  searchText?: string
): QueryObserverResult<AllTravelRequestsResults, unknown> {
  return useQuery({
    queryKey: [
      QueryKeys.ALL_TRAVEL_REQUESTS,
      dateFrom,
      dateTo,
      status,
      searchText,
    ],
    queryFn: () => getAllTravelRequests(dateFrom, dateTo, status, searchText),
    enabled: true,
    refetchOnWindowFocus: false,
    retry: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja ferðabeiðnir. Reyndu aftur síðar',
    },
  });
}
